// Actions
// Constants
import { TaskGroupingOption, UnbucketedTaskGroup } from "@ms/uno-constants/lib/local/GroupingConstants";
import { StoreEntityState } from "@ms/uno-constants/lib/local/StoreConstants";
import { ViewName, ViewRenderState } from "@ms/uno-constants/lib/local/ViewConstants";
import { BulkUpdatableField } from "@ms/planner-models/lib/local/client/planner/task/Enumerations";
import { getRecurrenceDropdownTypeFromPattern } from "@ms/planner-models/lib/local/client/planner/recurrence/utilities/RecurrencePatternUtilities";
import { getTaskFilterKey } from "@ms/planner-models/lib/local/utilities/FilterUtilities";
import { getSortCriteriaKey } from "@ms/planner-models/lib/local/utilities/SortCriteriaUtilities";
import { taskHoldsBulkUpdate } from "@ms/planner-models/lib/local/utilities/TaskUtilities";
import { Store } from "../stores/Store";
/**
 * Whether the key names no work to do, such as an empty list of task ids.
 * Without this guard an interaction with nothing to verify would report itself complete on the next
 * unrelated repaint, timing whatever that repaint was doing.
 * @param key What the interaction would be started with
 */ export function namesNoWork(key) {
    if (typeof key === "string") {
        return key.length === 0;
    }
    if (Array.isArray(key)) {
        return key.length === 0;
    }
    if (typeof key === "object" && key !== null && "expectedTaskCounts" in key) {
        return key.expectedTaskCounts.length === 0;
    }
    if (typeof key === "object" && key !== null && "taskId" in key) {
        return key.taskId.length === 0 || "attachmentId" in key && key.attachmentId.length === 0 || "linkId" in key && key.linkId.length === 0 || "labelIds" in key && key.labelIds.length === 0;
    }
    return typeof key === "object" && key !== null && "taskIds" in key && key.taskIds.length === 0;
}
function getGridPasteRowKey(task) {
    return JSON.stringify([
        task.displayName,
        task.bucketId
    ]);
}
/** Capture the expected result before dispatching paste; duplicate rows require additional matches. */ export function getGridPasteTasksInteractionKey(planId, existingTasks, pastedTasks) {
    const counts = new Map();
    for (const task of pastedTasks){
        if (task.planId !== planId) {
            continue;
        }
        const key = getGridPasteRowKey(task);
        counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    for (const task of existingTasks){
        if (task.planId !== planId) {
            continue;
        }
        const key = getGridPasteRowKey(task);
        const count = counts.get(key);
        if (count !== undefined) {
            counts.set(key, count + 1);
        }
    }
    return {
        planId,
        expectedTaskCounts: [
            ...counts
        ]
    };
}
/**
 * Every tracked interaction, keyed by name. Lives here rather than in telemetry because these checks
 * read stores, and telemetry sits below the store layer.
 *
 * Each entry narrows on `interactionId.name` before reading `key`.
 *
 * To add one: name it in `TrackedInteractionName`, declare its key in `IInteractionKeyOf`, then add an
 * entry here.
 */ export const TrackedInteractions = {
    /** Opening My Plans. Completed by the existing page render signal. */ OpenMyPlans: {
        isComplete (interactionId, stores, targetViewId, renderState) {
            return renderState != null && renderState >= ViewRenderState.Critical;
        }
    },
    MoveTask: {
        isComplete (interactionId, _stores, targetViewId, renderState) {
            return interactionId.name === "MoveTask" && targetViewId.name === ViewName.TaskMoveToast && targetViewId.partitionKey === JSON.stringify(interactionId.key) && renderState === ViewRenderState.All;
        }
    },
    CopyTask: {
        isComplete (interactionId, _stores, targetViewId, renderState) {
            // The pre-start probe is not a toast render.
            if (renderState == null) {
                return false;
            }
            return interactionId.name === "CopyTask" && targetViewId.name === ViewName.TaskCopyToast;
        }
    },
    CopyTaskLink: {
        isComplete (interactionId, _stores, targetViewId, renderState) {
            return interactionId.name === "CopyTaskLink" && targetViewId.name === ViewName.TaskLinkToast && targetViewId.partitionKey === interactionId.key && renderState === ViewRenderState.All;
        }
    },
    /**
     * The interaction tracker hook is not exposed to generic controls. The presentational label controls arm
     * EditLabel directly in their existing onChangeText callbacks, before rename processing, using the owning
     * view identity. EditableLabel and generic LabelEditor remain unchanged. Complete when the owner renders
     * the submitted label ID/name in PlanStore.
     * Timing starts at the post-dismissal onChange notification, not the original Enter/blur event.
     */ EditLabel: {
        isComplete (interactionId, stores, targetViewId, renderState) {
            if (interactionId.name !== "EditLabel" || renderState == null || renderState < ViewRenderState.Critical) {
                return false;
            }
            const { labelId, displayName } = interactionId.key;
            const planId = targetViewId.name === ViewName.TaskEditorContainer ? stores.load(Store.Tasks).getTask(targetViewId.partitionKey).data?.planId : targetViewId.partitionKey;
            if (!planId) {
                return false;
            }
            return stores.load(Store.Plan).getPlan(planId).data?.planView.categories.some((category)=>category.id === labelId && category.displayName === displayName) === true;
        }
    },
    /** Opening the task editor. Completed by the editor for that task rendering. */ OpenTaskEditor: {
        isComplete (interactionId, stores, targetViewId, renderState) {
            return renderState != null && renderState >= ViewRenderState.Critical;
        }
    },
    /** Setting or clearing a start date. Completed once every task reads back the requested value. */ SetStartDate: {
        isComplete (interactionId, stores) {
            if (interactionId.name !== "SetStartDate") {
                return false;
            }
            const { taskIds, startDateTime } = interactionId.key;
            return taskIds.every((taskId)=>{
                const task = stores.load(Store.Tasks).getTask(taskId).data;
                return startDateTime == null ? task != null && task.startDateTime == null : taskHoldsBulkUpdate(task, BulkUpdatableField.StartDateTime, startDateTime);
            });
        }
    },
    /** Setting recurrence. Completed when the task reads back the selected repeat option. */ SetRecurrence: {
        isComplete (interactionId, stores) {
            if (interactionId.name !== "SetRecurrence") {
                return false;
            }
            const task = stores.load(Store.Tasks).getTask(interactionId.key.taskId).data;
            const pattern = task?.recurrence?.schedule?.pattern;
            return task != null && getRecurrenceDropdownTypeFromPattern(pattern) === interactionId.key.recurrenceOption && (pattern?.generateHash() ?? null) === interactionId.key.recurrencePatternHash;
        }
    },
    /** Opening My Day. Completed when the page reaches its critical render state. */ OpenMyDay: {
        isComplete (interactionId, stores, targetViewId, renderState) {
            return renderState != null && renderState >= ViewRenderState.Critical;
        }
    },
    /** Switching task-editor tabs. Completed once the selected tab is persisted in view state. */ SwitchTaskDetailTab: {
        isComplete (interactionId, stores) {
            if (interactionId.name !== "SwitchTaskDetailTab") {
                return false;
            }
            return stores.load(Store.View).getTaskEditorSelectedTab(interactionId.key.taskId) === interactionId.key.selectedTab;
        }
    },
    /** Opening the Copilot pane. Completed by the pane rendering. */ OpenCatalystPane: {
        isComplete (interactionId, stores, targetViewId, renderState) {
            return renderState != null && renderState >= ViewRenderState.Critical;
        }
    },
    /** Opening the My Portfolios page. Completed by the page rendering. */ OpenMyPortfolios: {
        isComplete (interactionId, stores, targetViewId, renderState) {
            return renderState != null && renderState >= ViewRenderState.Critical;
        }
    },
    /**
     * Opening the My Tasks page. Completed by the page rendering enough tasks to be worth looking at.
     *
     * Keyed by the pivot the page is opened on, since that is how it partitions its render state.
     * Moving between pivots and view types once the page is open is not this interaction: the page
     * stays mounted across those switches, so they produce no new render signal.
     */ OpenMyTasks: {
        isComplete (interactionId, stores, targetViewId, renderState) {
            return renderState != null && renderState >= ViewRenderState.Critical;
        }
    },
    /** Opening a plan page. Completed by the plan page rendering, regardless of which view it lands on. */ OpenPlan: {
        isComplete (interactionId, stores, targetViewId, renderState) {
            return renderState != null && renderState >= ViewRenderState.Critical;
        }
    },
    /** Creating a plan. Completed by the new plan page rendering. */ CreatePlan: {
        isComplete (interactionId, stores, targetViewId, renderState) {
            return renderState != null && renderState >= ViewRenderState.Critical;
        }
    },
    /** Opening Create Plan. Completed when its Microsoft templates are loaded. */ OpenCreatePlanModal: {
        isComplete (interactionId, stores, targetViewId, renderState) {
            return renderState === ViewRenderState.All;
        }
    },
    /**
     * Opening the share plan dialog. Completed once the dialog has the plan, its container and the
     * container's members, which is when it can show who the plan is already shared with rather than
     * an empty people list.
     */ OpenSharePlanDialog: {
        isComplete (interactionId, stores, targetViewId, renderState) {
            return renderState != null && renderState >= ViewRenderState.Critical;
        }
    },
    /** Toggling a plan's pin state. Completed once optimistic MRU state matches the requested state. */ TogglePin: {
        isComplete (interactionId, stores) {
            if (interactionId.name !== "TogglePin") {
                return false;
            }
            return stores.load(Store.Mru).getMruItemByServerDocId(interactionId.key.planId)?.isPinned === interactionId.key.isPinned;
        }
    },
    /** Moving a Board task. Completed once the committed Board view shows it only in the destination bucket. */ TaskBucketIdUpdated: {
        isComplete (interactionId, stores) {
            if (interactionId.name !== "TaskBucketIdUpdated") {
                return false;
            }
            const taskStore = stores.load(Store.Tasks);
            const { taskId, sourceBucketId, destinationBucketId, boardViewId } = interactionId.key;
            const isInDestination = taskStore.getTasksForBoardViewGroup(boardViewId, TaskGroupingOption.Bucket, destinationBucketId).some((task)=>task.id === taskId);
            const isInSource = taskStore.getTasksForBoardViewGroup(boardViewId, TaskGroupingOption.Bucket, sourceBucketId).some((task)=>task.id === taskId);
            return isInDestination && !isInSource;
        }
    },
    /** Adding a comment. Approximated by a conversation render containing matching content from the submitting author since submission. */ AddComment: {
        isComplete (interactionId, stores, targetViewId, renderState) {
            if (interactionId.name !== "AddComment" || renderState === ViewRenderState.None) {
                return false;
            }
            const { userId, content, submittedAt } = interactionId.key;
            return stores.load(Store.Chat).getChatMessagesForTask(targetViewId.partitionKey).data.some((message)=>!message.isDeleted && message.createdBy.user.id === userId && message.createdDateTime.epochMilliseconds >= submittedAt && message.content === content);
        }
    },
    /** Reordering a bucket. Completed when a render or repaint observes its requested predecessor in Board order. */ ReorderBucket: {
        isComplete (interactionId, stores, targetViewId, renderState) {
            if (interactionId.name !== "ReorderBucket" || renderState === ViewRenderState.None) {
                return false;
            }
            const { bucketId, sortAfter } = interactionId.key;
            const buckets = stores.load(Store.Bucket).getBucketsForPlan(targetViewId.partitionKey).data;
            const bucketIndex = buckets.findIndex((bucket)=>bucket.id === bucketId);
            return bucketIndex >= 0 && (buckets[bucketIndex - 1]?.id ?? null) === (sortAfter === UnbucketedTaskGroup ? null : sortAfter);
        }
    },
    /** Renaming a bucket. Completed when a content render observes the submitted title. */ RenameBucket: {
        isComplete (interactionId, stores, _targetViewId, renderState) {
            if (interactionId.name !== "RenameBucket" || renderState === ViewRenderState.None) {
                return false;
            }
            return stores.load(Store.Bucket).getBucket(interactionId.key.bucketId).data?.displayName === interactionId.key.displayName;
        }
    },
    /** Renaming a task. Completed when a content render or repaint observes the submitted title. */ RenameTask: {
        isComplete (interactionId, stores, _targetViewId, renderState) {
            if (interactionId.name !== "RenameTask" || renderState === ViewRenderState.None) {
                return false;
            }
            return stores.load(Store.Tasks).getTask(interactionId.key.taskId).data?.displayName === interactionId.key.displayName;
        }
    },
    /** Creating a task. Completed from optimistic state on a content render or repaint, never a loading-only render. */ CreateTask: {
        isComplete (interactionId, stores, _targetViewId, renderState) {
            if (interactionId.name !== "CreateTask" || renderState === ViewRenderState.None) {
                return false;
            }
            return stores.load(Store.Tasks).getTask(interactionId.key.taskId).data != null;
        }
    },
    /** Adding a file attachment. Completed when the uploaded attachment is persisted and visible. */ AddAttachment: {
        isComplete (interactionId, stores, _targetViewId, renderState) {
            if (interactionId.name !== "AddAttachment" || renderState === ViewRenderState.None) {
                return false;
            }
            const taskStore = stores.load(Store.Tasks);
            const task = taskStore.getTask(interactionId.key.taskId, true).data;
            return task?.links.some((link)=>link.id === interactionId.key.attachmentId) ?? false;
        }
    },
    /** Adding a task link. Completed when a content render observes the client-generated link. */ AddTaskLink: {
        isComplete (interactionId, stores, _targetViewId, renderState) {
            if (interactionId.name !== "AddTaskLink" || renderState === ViewRenderState.None) {
                return false;
            }
            const task = stores.load(Store.Tasks).getTask(interactionId.key.taskId).data;
            return task?.links.some((link)=>link.id === interactionId.key.linkId) ?? false;
        }
    },
    /** Editing a task link. Completed when a content render observes the submitted display name. */ EditTaskLink: {
        isComplete (interactionId, stores, _targetViewId, renderState) {
            if (interactionId.name !== "EditTaskLink" || renderState === ViewRenderState.None) {
                return false;
            }
            const task = stores.load(Store.Tasks).getTask(interactionId.key.taskId).data;
            return task?.links.some((link)=>link.id === interactionId.key.linkId && link.displayName === interactionId.key.displayName) ?? false;
        }
    },
    /** Editing task notes. Completed once the optimistic task holds the submitted HTML content. */ EditTaskNotes: {
        isComplete (interactionId, stores) {
            if (interactionId.name !== "EditTaskNotes") {
                return false;
            }
            const task = stores.load(Store.Tasks).getTask(interactionId.key.taskId).data;
            return task != null && (task.notes?.content ?? "") === interactionId.key.content;
        }
    },
    /** Assigning or clearing a due date. Completed once every task holds the requested value. */ AssignDueDate: {
        isComplete (interactionId, stores) {
            if (interactionId.name !== "AssignDueDate") {
                return false;
            }
            const { taskIds, dueDateTime } = interactionId.key;
            return taskIds.every((taskId)=>{
                const task = stores.load(Store.Tasks).getTask(taskId).data;
                return dueDateTime == null ? task != null && task.dueDateTime == null : taskHoldsBulkUpdate(task, BulkUpdatableField.DueDateTime, dueDateTime);
            });
        }
    },
    /** Grouping a task surface. Completed once the target view holds the requested grouping clause. */ GroupBy: {
        isComplete (interactionId, stores, targetViewId) {
            if (interactionId.name !== "GroupBy") {
                return false;
            }
            return stores.load(Store.View).getCurrentTaskGroupingState(targetViewId) === interactionId.key;
        }
    },
    /** Sorting the grid. Completed once the view holds the requested sort. */ GridSort: {
        isComplete (interactionId, stores, targetViewId) {
            if (interactionId.name !== "GridSort") {
                return false;
            }
            const applied = stores.load(Store.View).getSortCriteria(targetViewId);
            // Same object on the common path; keying is the fallback for a criteria rebuilt elsewhere.
            return applied === interactionId.key || getSortCriteriaKey(applied) === getSortCriteriaKey(interactionId.key);
        }
    },
    /** Filtering the grid from the Filter by menu. Completed once the view holds the requested filter. */ FilterByMenu: {
        isComplete (interactionId, stores, targetViewId) {
            if (interactionId.name !== "FilterByMenu") {
                return false;
            }
            const applied = stores.load(Store.View).getCurrentTaskFilter(targetViewId);
            // The view stores the very object the caller passed, so this hits on the common path and
            // keeps a dynamic filter's task-id list from being keyed on every repaint.
            return applied === interactionId.key || getTaskFilterKey(applied) === getTaskFilterKey(interactionId.key);
        }
    },
    /**
     * Paste plan-data completion, checked on the target grid's render/repaint. Counts survive
     * temporary ID replacement. This does not prove request identity or filtered-row visibility:
     * concurrent additions with identical titles and buckets can satisfy the same expected counts.
     */ GridPasteTasks: {
        isComplete (interactionId, stores, _targetViewId, renderState) {
            if (interactionId.name !== "GridPasteTasks" || renderState === ViewRenderState.None || interactionId.key.expectedTaskCounts.length === 0) {
                return false;
            }
            const { planId, expectedTaskCounts } = interactionId.key;
            const remaining = new Map(expectedTaskCounts);
            for (const task of stores.load(Store.Tasks).getTasksForPlan(planId).data){
                if (task.planId !== planId) {
                    continue;
                }
                const key = getGridPasteRowKey(task);
                const count = remaining.get(key);
                if (count === undefined) {
                    continue;
                }
                if (count === 1) {
                    remaining.delete(key);
                } else {
                    remaining.set(key, count - 1);
                }
                if (remaining.size === 0) {
                    return true;
                }
            }
            return false;
        }
    },
    /** Deleting rows. Completed once every deleted task has left the grid. */ GridDeleteRows: {
        isComplete (interactionId, stores) {
            if (interactionId.name !== "GridDeleteRows") {
                return false;
            }
            // Deletes are optimistic and the entity is kept in the table so undo can recover it, so
            // its data outliving the delete proves nothing. Status is what the grid reads: it leaves
            // Present the moment the row is taken off screen, before the server has answered.
            return interactionId.key.every((taskId)=>{
                const { data, status } = stores.load(Store.Tasks).getTask(taskId);
                return data == null || status.state === StoreEntityState.SoftDeleted || status.state === StoreEntityState.Absent;
            });
        }
    },
    /**
     * Deleting one task from a surface other than the grid. Completed once the task leaves the
     * surface, read from status rather than presence because an optimistic delete keeps the entity
     * in the store so undo can restore it. Same reading as {@link GridDeleteRows}.
     */ DeleteTask: {
        isComplete (interactionId, stores) {
            if (interactionId.name !== "DeleteTask") {
                return false;
            }
            const { data, status } = stores.load(Store.Tasks).getTask(interactionId.key);
            return data == null || status.state === StoreEntityState.SoftDeleted || status.state === StoreEntityState.Absent;
        }
    },
    /**
     * Deleting a bucket and the tasks in it. Completed once the bucket has left the rendered board,
     * when its status becomes SoftDeleted or Absent. Read the same way as {@link GridDeleteRows},
     * since a bucket delete is optimistic and the entity is kept so undo can restore it.
     */ DeleteBucket: {
        isComplete (interactionId, stores) {
            if (interactionId.name !== "DeleteBucket") {
                return false;
            }
            const { data, status } = stores.load(Store.Bucket).getBucket(interactionId.key);
            return data == null || status.state === StoreEntityState.SoftDeleted || status.state === StoreEntityState.Absent;
        }
    },
    /** Adding a bucket column. */ AddBucket: {
        isComplete (interactionId, stores, _targetViewId, renderState) {
            if (interactionId.name !== "AddBucket" || renderState == null || renderState < ViewRenderState.Critical) {
                return false;
            }
            // New bucket names are effectively unique during this render window; the optimistic ID is rekeyed out after service success.
            const matchingBucketExists = stores.load(Store.Bucket).getBucketsForPlan(interactionId.key.planId).data?.some((bucket)=>bucket.displayName === interactionId.key.displayName) ?? false;
            return matchingBucketExists && stores.load(Store.View).getCurrentTaskGroupingState({
                name: ViewName.PlanPage,
                partitionKey: interactionId.key.planId
            }) === TaskGroupingOption.Bucket;
        }
    },
    /**
     * Moving tasks by cut and paste. Checks position rather than presence, since a move creates
     * nothing and the tasks it names already exist.
     */ GridMoveTasks: {
        isComplete (interactionId, stores) {
            if (interactionId.name !== "GridMoveTasks") {
                return false;
            }
            return interactionId.key.every(({ taskId, orderHint })=>stores.load(Store.Tasks).getTask(taskId).data?.getPlanViewOrderHint(TaskGroupingOption.None) === orderHint);
        }
    },
    /** Updating a field across selected tasks. Completed once every task reads back the new value. */ GridBulkUpdate: {
        isComplete (interactionId, stores) {
            if (interactionId.name !== "GridBulkUpdate") {
                return false;
            }
            const { taskIds, field, value } = interactionId.key;
            return taskIds.every((taskId)=>taskHoldsBulkUpdate(stores.load(Store.Tasks).getTask(taskId).data, field, value));
        }
    },
    /** Completing a task. Completed when the target surface rerenders with the task complete. */ CompleteTask: {
        isComplete (interactionId, stores) {
            if (interactionId.name !== "CompleteTask") {
                return false;
            }
            return stores.load(Store.Tasks).getTask(interactionId.key).data?.isComplete() === true;
        }
    },
    /**
     * Setting a task's status, from a checkbox, a status menu or a drop onto a status column.
     * Completed once every task named reads the requested status back from the store, which is the
     * optimistic write, so this settles on the first repaint that shows it rather than on the
     * server round trip. Checking the status rather than a render state is what keeps an unrelated
     * re-render of the same view from ending the measurement early.
     */ SetTaskStatus: {
        isComplete (interactionId, stores) {
            if (interactionId.name !== "SetTaskStatus") {
                return false;
            }
            const { taskIds, status } = interactionId.key;
            return taskIds.every((taskId)=>taskHoldsBulkUpdate(stores.load(Store.Tasks).getTask(taskId).data, BulkUpdatableField.Status, status));
        }
    },
    /**
     * Setting a task's priority. A render signal from the owning surface is only accepted once every
     * affected task reads back the exact requested priority from TaskStore, so a re-render caused by
     * unrelated work cannot settle it early.
     */ SetTaskPriority: {
        isComplete (interactionId, stores) {
            if (interactionId.name !== "SetTaskPriority") {
                return false;
            }
            const { taskIds, priority } = interactionId.key;
            return taskIds.every((taskId)=>taskHoldsBulkUpdate(stores.load(Store.Tasks).getTask(taskId).data, BulkUpdatableField.Priority, priority));
        }
    },
    /**
     * Assigning a member to one or more tasks. A render/repaint signal from the owning surface is
     * only accepted once every affected task reads back the assigned user in its assignee list,
     * which is what the re-rendered surface shows.
     */ AssignMember: {
        isComplete (interactionId, stores) {
            if (interactionId.name !== "AssignMember") {
                return false;
            }
            const { taskIds, userId } = interactionId.key;
            return taskIds.every((taskId)=>stores.load(Store.Tasks).getTask(taskId).data?.getAssigneeIds().includes(userId) === true);
        }
    },
    /** Adding labels. Completes on the owning surface's render/repaint after all selected IDs are applied. */ AddLabel: {
        isComplete (interactionId, stores) {
            if (interactionId.name !== "AddLabel") {
                return false;
            }
            const { taskId, labelIds } = interactionId.key;
            const appliedLabelIds = stores.load(Store.Tasks).getTask(taskId).data?.getAppliedCategoryIds();
            return labelIds.length > 0 && appliedLabelIds != null && labelIds.every((id)=>appliedLabelIds.includes(id));
        }
    }
};
